import { useCallback, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import styled from "styled-components";
import { Plus, Upload } from "lucide-react";
import { Button, RouterButtonLink } from "../components/Button";
import { InfoBox } from "../components/InfoBox";
import { SidebarPanel } from "../components/SidebarPanel";
import { ProductForm } from "./components/ProductForm";
import { ProductsTableProvider } from "./components/products/ProductsTableProvider";
import {
  ProductsTablePanel,
  ProductsTableSaveWarning,
} from "./components/products/ProductsTablePanel";
import {
  useCreateProductMutation,
  useUpdateProductMutation,
  useDeleteProductMutation,
} from "../query/productsQuery";
import { useProductStore } from "../hooks/useProductStore";
import { useCatalogScope } from "../hooks/useCatalogScope";
import { useGetStoreQuery } from "../query/storesQuery";
import { useBulkUpsertProductOverlaysMutation } from "../query/productOverlaysQuery";
import {
  setStockBalancesApi,
  setStockPoliciesApi,
} from "../api/stockBalancesApi";
import type { ProductStockChanges } from "./components/products/useProductVariantStock";
import { borderRadius, spacing } from "@styling/sizing";
import colors from "@styling/colors";
import type { ProductId } from "@KassaroAB/shared/types/ids";
import type { RestProduct } from "@KassaroAB/shared/types/product";
import { useProductImportAccess } from "../hooks/useProductImportAccess";

const Layout = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.x2}px;
  height: 100%;
  padding: ${spacing.x2}px;
  box-sizing: border-box;

  @media (max-width: 768px) {
    padding: ${spacing.x1}px;
    gap: ${spacing.x1}px;
  }
`;

const Panel = styled.div`
  display: flex;
  flex-direction: column;
  flex: 1;
  min-height: 0;
  border: 1px solid ${colors.border};
  border-radius: ${borderRadius.regular}px;
  overflow: hidden;
  background-color: ${colors.backgroundLight};
`;

export const ProductsView = () => {
  const { t } = useTranslation();
  const { scope, storeId, pagePath } = useCatalogScope("products");

  const {
    store: storeRef,
    status,
    applyUpdated,
    version,
    resync,
    forceResync,
    lastSyncedAt,
    syncProgress,
  } = useProductStore(scope);

  const { data: store } = useGetStoreQuery(storeId ?? undefined);
  const importAccess = useProductImportAccess(scope);

  const { mutate: createMutation, isPending: isCreatePending } =
    useCreateProductMutation(scope);
  const { mutate: updateMutation, isPending: isUpdatePending } =
    useUpdateProductMutation(scope);
  const {
    mutate: deleteMutation,
    mutateAsync: deleteAsync,
    isPending: isDeletePending,
  } = useDeleteProductMutation(scope);

  const { mutate: upsertOverlaysMutation, isPending: isOverlayPending } =
    useBulkUpsertProductOverlaysMutation(storeId ?? null);
  const [overlaySaveError, setOverlaySaveError] = useState<string | null>(null);

  const queryClient = useQueryClient();
  const [stockSaveError, setStockSaveError] = useState(false);

  // Returns false when the product was saved but its stock was not.
  const saveStock = async (
    productId: ProductId,
    stock: ProductStockChanges,
  ): Promise<boolean> => {
    if (
      !storeId ||
      (stock.balances.length === 0 && stock.policies.length === 0)
    ) {
      return true;
    }
    const saved = await Promise.all([
      stock.balances.length > 0 &&
        setStockBalancesApi(
          storeId,
          stock.balances.map((balance) => ({ ...balance, productId })),
        ),
      stock.policies.length > 0 &&
        setStockPoliciesApi(
          storeId,
          stock.policies.map((policy) => ({ ...policy, productId })),
        ),
    ])
      .then(() => true)
      .catch(() => false);
    ["stockBalances", "stockWarnings"].forEach((collection) => {
      void queryClient.invalidateQueries({
        queryKey: ["stores", storeId, collection],
      });
    });
    setStockSaveError(!saved);
    return saved;
  };

  const [panelProduct, setPanelProduct] = useState<RestProduct | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [isPanelOpen, setIsPanelOpen] = useState(false);

  const stockSaveErrorBox = (
    <InfoBox variant="error">{t("productsView.panelStockSaveFailed")}</InfoBox>
  );

  const handlePanelClose = () => {
    setOverlaySaveError(null);
    setIsPanelOpen(false);
    setIsCreating(false);
    setPanelProduct(null);
  };

  const handleEditProduct = useCallback((product: RestProduct) => {
    setStockSaveError(false);
    setOverlaySaveError(null);
    setPanelProduct(product);
    setIsCreating(false);
    setIsPanelOpen(true);
  }, []);

  const deleteProducts = useCallback(
    async (products: RestProduct[]) => {
      await Promise.all(products.map((product) => deleteAsync(product.id)));
      await applyUpdated(
        products.map((product) => ({ ...product, isDeleted: true })),
      );
    },
    [applyUpdated, deleteAsync],
  );

  const toolbarActions = (
    <>
      {importAccess !== "locked" && importAccess !== "unknown" && (
        <RouterButtonLink
          to={`${pagePath}/import`}
          $color="secondary"
          $variant="outlined"
          $small
        >
          <Upload size={16} />
          {t("productsView.import")}
        </RouterButtonLink>
      )}
      <Button
        $small
        onClick={() => {
          setStockSaveError(false);
          setPanelProduct(null);
          setIsCreating(true);
          setIsPanelOpen(true);
        }}
      >
        <Plus size={16} />
        {t("productsView.create")}
      </Button>
    </>
  );

  return (
    <Layout>
      {status === "error" && (
        <InfoBox variant="error">{t("productsView.errorLoad")}</InfoBox>
      )}
      {stockSaveError && !isPanelOpen && stockSaveErrorBox}

      <ProductsTableProvider
        scope={scope}
        storeRef={storeRef}
        status={status}
        version={version}
        applyUpdated={applyUpdated}
        canEdit
        onEditProduct={handleEditProduct}
        resync={resync}
        forceResync={forceResync}
        lastSyncedAt={lastSyncedAt}
        syncProgress={syncProgress}
        toolbarActions={toolbarActions}
        deleteProducts={deleteProducts}
      >
        <ProductsTableSaveWarning />

        <Panel>
          <ProductsTablePanel />
        </Panel>

        {(panelProduct || isCreating) && (
          <SidebarPanel
            wide
            isOpen={isPanelOpen}
            onClose={handlePanelClose}
            title={
              isCreating
                ? t("productsView.panelCreate")
                : t("productsView.panelEdit", { name: panelProduct?.name ?? "" })
            }
          >
            {stockSaveError && stockSaveErrorBox}
            {overlaySaveError && (
              <InfoBox variant="error">
                {t("productsView.panelOverlaySaveFailed", {
                  error: overlaySaveError,
                })}
              </InfoBox>
            )}
            <ProductForm
              key={`${panelProduct?.id ?? "new"}-${store?.extraProductFields?.length ?? 0}`}
              product={panelProduct || undefined}
              onCreate={(data, stock) => {
                createMutation(data, {
                  onSuccess: (created) => {
                    void applyUpdated([created]);
                    // A new product's form cannot be reopened, so a stock
                    // error is only reported.
                    void saveStock(created.id, stock);
                    handlePanelClose();
                  },
                });
              }}
              onUpdate={(data, stock) => {
                if (!panelProduct) return;
                updateMutation(
                  { productId: panelProduct.id, product: data },
                  {
                    onSuccess: (updated) => {
                      void applyUpdated([updated]);
                      // Keep the panel, and its stock edits, open to retry.
                      void saveStock(updated.id, stock).then((saved) => {
                        if (saved) handlePanelClose();
                      });
                    },
                  },
                );
              }}
              onUpdateOverlay={(overlay, stock) => {
                if (!panelProduct) return;
                setOverlaySaveError(null);
                const saveStockAndClose = (productId: ProductId) =>
                  void saveStock(productId, stock).then((saved) => {
                    if (saved) handlePanelClose();
                  });
                if (Object.keys(overlay).length === 0) {
                  saveStockAndClose(panelProduct.id);
                  return;
                }
                upsertOverlaysMutation(
                  [{ productId: panelProduct.id, overlay }],
                  {
                    onSuccess: (result) => {
                      void applyUpdated(result.updated);
                      if (result.failed.length > 0) {
                        setOverlaySaveError(result.failed[0].error);
                        return;
                      }
                      saveStockAndClose(panelProduct.id);
                    },
                    onError: (error) => setOverlaySaveError(error.message),
                  },
                );
              }}
              onCancel={handlePanelClose}
              onDelete={
                panelProduct && !isCreating
                  ? () => {
                      if (window.confirm(t("productsView.deleteConfirm"))) {
                        const target = panelProduct;
                        deleteMutation(target.id, {
                          onSuccess: () => {
                            void applyUpdated([{ ...target, isDeleted: true }]);
                            handlePanelClose();
                          },
                        });
                      }
                    }
                  : undefined
              }
              isLoading={
                isCreatePending ||
                isUpdatePending ||
                isDeletePending ||
                isOverlayPending
              }
            />
          </SidebarPanel>
        )}
      </ProductsTableProvider>
    </Layout>
  );
};
